import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import http from '@/libs/http'
import dayjs from 'dayjs'
import { toErrorMessage } from '@/libs/errorToaster'

export interface IModule {
	_id: string
	// hubids?: string[]
	// other module-specific fields
	[key: string]: any
}

export const useModuleStore = defineStore('module', () => {
	// --- STATE ---
	const modules = ref<IModule[]>([])
	const currentModul = ref<IModule | null>(null)
	const loading = ref<Record<string, boolean>>({})

	// --- HELPERS ---

	// MUST return string. Earlier version returned `response.data` raw when it
	// was an object → toast rendered `[object Object]`. Use the shared helper.
	function formatError(error: any): string {
		return toErrorMessage(error, 'An unexpected error occurred in module store')
	}
	async function withLoading<T>(key: string, fn: () => Promise<T>): Promise<T> {
		if (loading.value[key]) {
			throw new Error(`${key} is already in progress (modul)`)
		}
		try {
			loading.value[key] = true
			return await fn()
		} finally {
			loading.value[key] = false
		}
	}

	// --- GETTERS ---
	const modulesByHubSerial = computed(() => {
		const result: Record<string, IModule[]> = {}
		for (const module of modules.value) {
			const hubSerial = module.currentHub || 'NO_HUB'
			if (!result[hubSerial]) result[hubSerial] = []
			result[hubSerial].push(module)
		}
		console.log(result)
		return result
	})

	// --- ACTIONS ---

	// async function listModulesByHubsIds(projectId: string, hubids: string[]) {
	// 	return withLoading('listModulesByHubsIds', async () => {
	// 		try {
	// 			const resp = await http.getAuth<IModule[]>(`/api/v1/modules/list/${projectId}`, {
	// 				headers: { hubids: hubids.join(',') },
	// 			} as any)
	// 			modules.value = resp || []
	// 			return modules.value
	// 		} catch (error: any) {
	// 			console.error(error)
	// 			return []
	// 		}
	// 	})
	// }

	// async function setModuleInterval(data: object) {
	// 	return withLoading('setModuleInterval', async () => {
	// 		try {
	// 			const resp = await http.postAuth<any, object>('/api/v1/modules/setModulInterval', data)
	// 			return resp
	// 		} catch (error: any) {
	// 			console.error(error)
	// 			return null
	// 		}
	// 	})
	// }

	// async function setAverageInterval(data: object) {
	// 	return withLoading('setAverageInterval', async () => {
	// 		try {
	// 			const resp = await http.postAuth<any, object>('/api/v1/modules/setAverageInterval', data)
	// 			return resp
	// 		} catch (error: any) {
	// 			console.error(error)
	// 			return null
	// 		}
	// 	})
	// }

	async function fetchChartData(modulId: string) {
		return withLoading('fetchChartData', async () => {
			try {
				return await http.getAuth<any[]>(`/api/v2/modules/logs/${modulId}`)
			} catch (error: any) {
				console.error(error)
				return []
			}
		})
	}

	async function fetchChartTimearea(modulId: string, from: string | number, to: string | number) {
		return withLoading('fetchChartTimearea', async () => {
			try {
				return await http.getAuth<any[]>(`/api/v2/modules/timearea/${modulId}/${from}/${to}`)
			} catch (error: any) {
				console.error(error)
				return []
			}
		})
	}

	async function listAllModules() {
		return withLoading('listAllModules', async () => {
			try {
				console.log('🍍 listAllModules')
				const resp = await http.getAuth<IModule[]>('/api/v2/modules')
				modules.value = resp || []
				return modules.value
			} catch (error: any) {
				console.error(error)
				return []
			}
		})
	}
	// modul-sensor-settings-modal
	// async function listModuleData(modulId: string) {
	async function getModule(modulId: string) {
		return withLoading('listModuleData', async () => {
			try {
				const resp = await http.getAuth<IModule>(`/api/v2/module/${modulId}`)
				currentModul.value = resp || null
				return
			} catch (error: any) {
				console.error(error)
				return []
			}
		})
	}

	async function updateModule(data: IModule) {
		return withLoading('updateModule', async () => {
			try {
				const resp = await http.postAuth<{ msg: string }, IModule>('/api/v1/modules/update', data)
				await listAllModules()
				return resp.msg
			} catch (error: any) {
				console.error(error)
				throw new Error(formatError(error))
			}
		})
	}

	async function deleteLogFromModule(data: object) {
		return withLoading('deleteLogFromModule', async () => {
			try {
				const resp = await http.postAuth<{ msg: string }, object>('/api/v1/modules/deletelog', data)
				return resp.msg
			} catch (error: any) {
				console.error(error)
				throw new Error(formatError(error))
			}
		})
	}

	async function updateLogFromModule(data: object) {
		return withLoading('updateLogFromModule', async () => {
			try {
				const resp = await http.postAuth<{ msg: string }, object>('/api/v1/modules/updatelog', data)
				return resp.msg
			} catch (error: any) {
				console.error(error)
				throw new Error(formatError(error))
			}
		})
	}
	//! Export CSV data:

	async function makeLink(data: {
		from: string | number
		to: string | number
		format: 'csv' | 'json' | 'tsv'
		data: any
		graphName?: string
	}) {
		const { format, from, to, data: csvData, graphName } = data
		let content = ''
		if (format === 'csv') content = 'data:text/csv;charset=utf-8,' + csvData
		else if (format === 'json') content = 'data:application/json;charset=utf-8,' + JSON.stringify(csvData)
		else if (format === 'tsv') content = 'data:text/tsv;charset=utf-8,' + csvData
		const safeName = (graphName ?? '').trim() || 'export'
		console.log('🟪 makeLink graphName:', JSON.stringify(graphName), '→ safeName:', safeName)
		const csvname = `graph-${safeName}-${dayjs().format('YYYY-MM-DD HH:mm:ss')}.${format}`

		const encodedUri = encodeURI(content)
		setTimeout(() => {
			const dlink: HTMLAnchorElement = document.createElement('a')
			dlink.href = encodedUri // Use the encoded URI for the link
			dlink.download = csvname // Set the download attribute with the filename
			dlink.onclick = function (e: MouseEvent) {
				// const that = this
				setTimeout(() => {
					window.URL.revokeObjectURL(this.href)
				}, 1000)
			}
			dlink.click()
			dlink.remove()
		}, 250)
	}

	async function downloadGraphCsvData(data: {
		graphId: string
		from: string | number
		to: string | number
		format: 'csv' | 'json' | 'tsv'
		graphName?: string
	}) {
		return withLoading('downloadGraphCsvData', async () => {
			try {
				const resp = await http.getAuth<any>(
					`/api/v2/export/${data.format}/graph/${data.graphId}/${data.from}/${data.to}`,
				)
				console.log('Graph CSV Data Response:', resp)
				//makeLink
				await makeLink({
					from: data.from,
					to: data.to,
					format: data.format,
					data: resp,
					graphName: data.graphName,
				})
				return resp.data || {}
			} catch (error: any) {
				console.error(error)
				throw new Error(formatError(error))
			}
		})
	}

	async function downloadModulCsvData(data: {
		modulId: string
		from: string | number
		to: string | number
		format: 'csv' | 'json' | 'tsv'
		graphName?: string
	}) {
		return withLoading('downloadModulCsvData', async () => {
			try {
				const resp = await http.getAuth<any>(
					`/api/v2/export/${data.format}/modul/${data.modulId}/${data.from}/${data.to}`,
				)
				console.log('Graph CSV Data Response:', resp)
				//makeLink:
				await makeLink({
					from: data.from,
					to: data.to,
					format: data.format,
					data: resp,
					graphName: data.graphName,
				})
				return resp.data || {}
			} catch (error: any) {
				console.error(error)
				throw new Error(formatError(error))
			}
		})
	}

	// async _downloadGraphCsvData({ state, dispatch }: any, data: any) {
	// 	try {
	// 		const config = await dispatch('_config')
	// 		const url = `${env.apiUrl}/api/v2/export/${data.format}/graph/${data.graphId}/${data.from}/${data.to}/`
	// 		config.headers['timekey'] = data.timekey || ''
	// 		const resp = await axios.get(url, config)
	// 		//makeLink
	// 		dispatch('makeLink', {
	// 			modulId: null,
	// 			graphId: data.graphId,
	// 			from: data.from,
	// 			to: data.to,
	// 			format: data.format,
	// 			data: resp.data,
	// 			graphName: data.graphName,
	// 		})
	// 		return resp.data || {}
	// 	} catch (error: any) {
	// 		console.error(error)
	// 		const msg = error.response?.data?.msg || error.response?.data || error.message
	// 		µUtils.µ_error(msg)
	// 		return []
	// 	}
	// },

	// async _downloadModulCsvData({ state, dispatch }: any, data: any) {
	// 	try {
	// 		const config = await dispatch('_config')
	// 		const url = `${env.apiUrl}/api/v2/export/${data.format}/modul/${data.modulId}/${data.from}/${data.to}`
	// 		console.log(url)
	// 		const resp = await axios.get(url, config)
	// 		//makeLink:
	// 		dispatch('makeLink', { modulId: data.modulId, from: data.from, to: data.to, format: data.format, data: resp.data, graphName: data.graphName })
	// 		return resp.data || {}
	// 	} catch (error: any) {
	// 		console.error(error)
	// 		const msg = error.response?.data?.msg || error.response?.data || error.message
	// 		µUtils.µ_error(msg)
	// 		return []
	// 	}
	// },

	// --- INITIAL LOAD CAN BE TRIGGERED IN COMPONENTS ---


	return {
		modules,
		loading,

		modulesByHubSerial,

		// listModulesByHubsIds,
		// setModuleInterval,
		// setAverageInterval,
		fetchChartData,
		fetchChartTimearea,

		listAllModules,
		// listModuleData,
		getModule,
		updateModule,
		deleteLogFromModule,
		updateLogFromModule,

		downloadGraphCsvData,
		downloadModulCsvData,
	}
})
